import { useState } from 'react';
import { parseISO } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';

function getTodayIST(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

function getCurrentTimeIST(): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());
}
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Clock, Loader2, AlertTriangle, CalendarX2 } from 'lucide-react';
import { leaveService } from '@/services/leave.service';
import { useLeaveBlockedDates } from '@/hooks/leave/useLeaveBlockedDates';
import { formatDate } from '@/lib/dateFormat';
import { toast } from 'sonner';
import type { ShortLeaveRequest } from '@pms/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  request?: ShortLeaveRequest;
}

// Upcoming blocked dates listed under the date picker
const BLOCKED_HINT_COUNT = 3;

function blockedMessage(reason: string | null): string {
  return `This date is blocked for leave${reason ? ` (${reason})` : ''}`;
}

function parseMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function formatDuration(mins: number): string {
  if (mins <= 0) return '0 min';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

export function ShortLeaveModal({ open, onClose, request }: Props) {
  const queryClient = useQueryClient();
  const isEdit = !!request;

  const [requestDate, setRequestDate] = useState(() => request?.request_date ?? getTodayIST());
  const [fromTime, setFromTime] = useState(request?.from_time.slice(0, 5) ?? '');
  const [toTime, setToTime] = useState(request?.to_time.slice(0, 5) ?? '');
  const [reason, setReason] = useState(request?.reason ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dates HR blocked for leave. A date kept as it was on edit stays allowed, since HR may
  // have blocked it after the request was made.
  const { blocked, isBlocked, firstBlockedIn } = useLeaveBlockedDates(open);
  const blockedDate =
    requestDate && !(isEdit && requestDate === request!.request_date)
      ? firstBlockedIn(requestDate, requestDate)
      : undefined;

  const durationMins =
    fromTime && toTime ? parseMinutes(toTime) - parseMinutes(fromTime) : 0;

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { request_date: requestDate, from_time: fromTime, to_time: toTime, reason };
      return isEdit
        ? leaveService.updateShortLeave(request!.id, payload)
        : leaveService.createShortLeave(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] });
      toast.success(isEdit ? 'Short leave request updated' : 'Short leave request submitted');
      handleClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? (isEdit ? 'Failed to update short leave' : 'Failed to submit short leave');
      toast.error(msg);
      if (msg.includes('already reviewed')) {
        queryClient.invalidateQueries({ queryKey: ['leave'] });
        handleClose();
      }
    },
  });

  function validate(): boolean {
    const e: Record<string, string> = {};
    const todayStr = getTodayIST();
    if (!requestDate) {
      e.requestDate = 'Date is required';
    } else if (requestDate < todayStr) {
      e.requestDate = 'Date cannot be in the past';
    } else if (blockedDate) {
      e.requestDate = blockedMessage(blockedDate.reason);
    }
    if (!fromTime) {
      e.fromTime = 'Start time is required';
    } else if (requestDate === todayStr && fromTime < getCurrentTimeIST()) {
      e.fromTime = 'Start time cannot be in the past';
    }
    if (!toTime) e.toTime = 'End time is required';
    if (fromTime && toTime) {
      if (parseMinutes(toTime) <= parseMinutes(fromTime)) {
        e.toTime = 'End time must be after start time';
      } else if (durationMins > 180) {
        e.toTime = 'Duration cannot exceed 3 hours';
      }
    }
    if (!reason.trim() || reason.trim().length < 3) {
      e.reason = 'Reason must be at least 3 characters';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit() {
    if (validate()) mutation.mutate();
  }

  function handleClose() {
    setRequestDate(getTodayIST());
    setFromTime('');
    setToTime('');
    setReason('');
    setErrors({});
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col gap-0">
        {/* Header */}
        <DialogHeader className="px-5 py-4 border-b border-border/60 text-left">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Clock className="h-4 w-4 text-primary" />
            {isEdit ? 'Edit Short Leave' : 'Request Short Leave'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Max 3 hours · sent to your department leader
          </p>
        </DialogHeader>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Date */}
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">
              Date <span className="text-destructive">*</span>
            </label>
            <DatePicker
              value={requestDate}
              onChange={(v) => { setRequestDate(v); setFromTime(''); setToTime(''); }}
              minDate={parseISO(getTodayIST())}
              disableDay={isBlocked}
            />
            {errors.requestDate ? (
              <p className="text-xs text-destructive mt-1">{errors.requestDate}</p>
            ) : blockedDate ? (
              <p className="text-xs text-destructive mt-1">{blockedMessage(blockedDate.reason)}</p>
            ) : blocked.length > 0 && (
              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground mt-1.5">
                <CalendarX2 className="h-3.5 w-3.5 mt-px shrink-0" />
                <span>
                  Leave can't be applied on:{' '}
                  {blocked.slice(0, BLOCKED_HINT_COUNT).map((b, i) => (
                    <span key={b.id}>
                      {i > 0 && ', '}
                      <span className="font-medium text-foreground">{formatDate(b.blocked_date)}</span>
                      {b.reason && ` (${b.reason})`}
                    </span>
                  ))}
                  {blocked.length > BLOCKED_HINT_COUNT && ` +${blocked.length - BLOCKED_HINT_COUNT} more`}
                </span>
              </p>
            )}
          </div>

          {/* Time range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">
                From Time <span className="text-destructive">*</span>
              </label>
              <TimePicker
                value={fromTime}
                onChange={(v) => {
                  setFromTime(v);
                  if (toTime && parseMinutes(v) >= parseMinutes(toTime)) setToTime('');
                }}
                min={requestDate === getTodayIST() ? getCurrentTimeIST() : undefined}
                modal
                placeholder="From time"
                className={errors.fromTime ? 'border-destructive' : ''}
              />
              {errors.fromTime && (
                <p className="text-xs text-destructive mt-1">{errors.fromTime}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">
                To Time <span className="text-destructive">*</span>
              </label>
              <TimePicker
                value={toTime}
                onChange={setToTime}
                min={fromTime || undefined}
                modal
                placeholder="To time"
                className={errors.toTime ? 'border-destructive' : ''}
              />
              {errors.toTime && (
                <p className="text-xs text-destructive mt-1">{errors.toTime}</p>
              )}
            </div>
          </div>

          {/* Duration badge */}
          {durationMins > 0 && durationMins <= 180 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Duration:</span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                <Clock className="w-3 h-3" />
                {formatDuration(durationMins)}
              </span>
            </div>
          )}

          {durationMins > 180 && (
            <div className="flex items-center gap-2 rounded-lg bg-destructive/5 border border-destructive/30 px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
              <span className="text-xs text-destructive font-medium">
                Duration ({formatDuration(durationMins)}) exceeds 3 hours limit
              </span>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">
              Reason <span className="text-destructive">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for short leave..."
              rows={3}
              className={`resize-none text-sm ${errors.reason ? 'border-destructive' : ''}`}
            />
            {errors.reason && (
              <p className="text-xs text-destructive mt-1">{errors.reason}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">{reason.length}/500</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-border/60">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            disabled={mutation.isPending || durationMins > 180 || !!blockedDate}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Submit Request'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
